/* eslint react/prop-types: 0 */
import { useState, useCallback, useMemo } from 'react';
import { useCurrentSite } from '@hotjar/site';
import styled from '@emotion/styled';
import { removeAt } from '@hotjar/insights-common/lib/arrays';
import { IconButton, Switch, tokens } from '@hotjar/hotsauce';
// Importing directly from @hotjar/feedback is causing a strange module resolution error in tests
// so we are importing from the full paths here
import {
  QuestionContainer,
  QuestionFooter,
} from '@hotjar/feedback/components/Surveys/Wizard/Questions/QuestionsSection/QuestionContainer';

import { ReactionStyleSelector } from '@hotjar/feedback/components/Surveys/Wizard/Questions/ReactionStyleSelector/ReactionStyleSelector';
import { NextQuestionSelector } from '@hotjar/feedback/components/Surveys/Wizard/Questions/NextQuestionSelector';
import { useGate, useTierForGate } from '@hotjar/allowances';

import { ScaleLabels } from '@hotjar/feedback/components/Surveys/ScaleLabels/ScaleLabels';

import { QuestionTypeSelector } from '@hotjar/feedback';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { QUESTIONS_WITH_TEXT_INPUT } from '@hotjar/feedback/domain/Surveys/Questions/QuestionTypes';
import { AddImageButton } from '@hotjar/feedback/components/Surveys/Wizard/Questions/AddImageButton';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { useDebounce } from '@hotjar/webapp/hooks/useDebounce';
import { TextField } from './components/TextField';
import { Answers } from './QuestionOptions/Answers';
import { Description } from './QuestionOptions/Description';
import { CLOSE_ENDED_DEFAULT_ANSWERS, DEFAULT_LABELS_BY_TYPE } from './constants';
import {
  isCloseEndedType,
  isReactionType,
  isScaleType,
  isTitleAndDescriptionType,
  validateQuestion,
} from './questionValidation';

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const QuestionWrapper = styled.div`
  max-width: calc(${tokens.spacer.x1} * 73);
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: ${tokens.spacer.x2pt5};
`;

const QuestionActions = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSwitch = styled(Switch)`
  margin-right: ${tokens.spacer.x2};
  margin-bottom: 0;
  flex-direction: row-reverse;
  gap: ${tokens.spacer.x1};
  ${({ labelOff, labelOn }) =>
    !(labelOff || labelOn)
      ? `
  span {
    margin-top: 0;
  }
  `
      : ''}
`;

const ReactionStyle = ({ question, updateQuestion, onPreviewReaction }) => {
  const { data: site } = useCurrentSite();
  const context = {
    type: 'site',
    id: site?.id,
  };
  const hasAdditionalReactionOptions = useGate('feedback.emotion_style_choices', context);

  const planRequiredForAdditionalReactionOptions = useTierForGate(
    'feedback.emotion_style_choices',
    context,
  );
  const handleReactionStyleChange = useCallback(
    (style) => {
      updateQuestion({ reaction_style: style });
    },
    [updateQuestion],
  );

  const handleReactionMouseEnter = useCallback(
    (style) => {
      onPreviewReaction({ questionId: question.uuid, style });
    },
    [question, onPreviewReaction],
  );

  const handleReactionMouseLeave = useCallback(() => {
    onPreviewReaction(null);
  }, [onPreviewReaction]);

  const showReactionStyleUpsell = useCallback(() => {
    upsell({ referrer: 'survey.emotion_style_choices', site });
  }, [site]);

  return (
    <ReactionStyleSelector
      reactionStyle={question.reaction_style}
      onStyleChange={handleReactionStyleChange}
      onMouseEnterStyle={handleReactionMouseEnter}
      onMouseLeaveStyle={handleReactionMouseLeave}
      showUpsell={showReactionStyleUpsell}
      hasAdditionalOptions={hasAdditionalReactionOptions}
      additionalOptionsPlan={planRequiredForAdditionalReactionOptions}
    />
  );
};

export const Question = ({
  question: initialQuestion,
  questions,
  onQuestionUpdate,
  onPreviewReaction,
  canChangeQuestionType,
  index,
  removable,
  onRemove,
  rtl,
  validate,
  isDragging,
  reloadPreview,
}) => {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.questions');
  const [question, setQuestion] = useState(initialQuestion);
  const hasImageFeature = useFeatureFlag('survey.image_question');

  const updateQuestion = useCallback((fieldsToUpdate) => {
    setQuestion((currentQuestion) => {
      const updatedQuestion = {
        ...currentQuestion,
        ...fieldsToUpdate,
        isTouched: true,
      };

      return updatedQuestion;
    });
  }, []);

  useDebounce(
    () => {
      const { isTouched, ...updatedQuestion } = question;
      if (onQuestionUpdate && isTouched === true) {
        const isQuestionValid = validateQuestion(question);
        onQuestionUpdate(updatedQuestion, question.uuid, isQuestionValid);
      }
    },
    500,
    [question],
  );

  // This function is called when question text input gets focused. In that case,
  // we just want to trigger the Preview to update without actually updating any
  // of the question fields, so we call `updateQuestion` with an empty object.
  const reloadPreviewCallback = useCallback(() => {
    reloadPreview(`question:${question.uuid}`);
  }, [reloadPreview, question]);

  const handleRequiredChange = (isRequired) => {
    updateQuestion({
      required: isRequired,
      nextIfSkipped: isRequired ? undefined : 'byOrder',
    });
  };

  const handleQuestionTextChange = (evt) => {
    updateQuestion({ text: evt.target.value });
  };

  const handleQuestionTypeChange = (questionType) => {
    let fieldsToUpdate = {
      type: questionType,
      randomize_answer_order: undefined,
      next: 'byOrder',
      nextByAnswer: undefined,
      labels: undefined,
      scaleCount: undefined,
      description: undefined,
      reaction_style: undefined,
    };

    // If question is changed to be one of close-ended types, we initialize default answers.
    // We only preserve answers if you are switching between single/multiple choice questions.
    if (isCloseEndedType(questionType)) {
      if (!question.answers) {
        fieldsToUpdate.answers = clone(CLOSE_ENDED_DEFAULT_ANSWERS);
      }
      fieldsToUpdate.randomize_answer_order = !!question.randomize_answer_order;
    } else {
      fieldsToUpdate.answers = undefined;
    }

    // Questions changed to scale-type questions get default pair of labels.
    if (isScaleType(questionType)) {
      fieldsToUpdate = { ...fieldsToUpdate, ...clone(DEFAULT_LABELS_BY_TYPE[questionType]) };

      if (isReactionType(questionType)) {
        fieldsToUpdate.reaction_style = 'default';
      }
    }

    if (isTitleAndDescriptionType(questionType)) {
      fieldsToUpdate.required = true;
      fieldsToUpdate.nextIfSkipped = undefined;
    }

    updateQuestion(fieldsToUpdate);
  };

  const handleAddAnswer = (answers) => {
    const fieldsToUpdate = {
      answers,
    };
    // If question.next is set to `byAnswer` adjust `nextByAnswerOptions` to include the new answer
    if (question.next === 'byAnswer') {
      fieldsToUpdate.nextByAnswer = [...question.nextByAnswer, 'byOrder'];
    }

    updateQuestion(fieldsToUpdate);
  };

  const handleDeleteAnswer = (answers, removeAtIndex) => {
    const fieldsToUpdate = {
      answers,
    };
    // If question.next is set to `byAnswer` adjust `nextByAnswerOptions` to exclude the deleted answer option
    if (question.next === 'byAnswer') {
      fieldsToUpdate.nextByAnswer = removeAt(question.nextByAnswer, removeAtIndex);
    }

    updateQuestion(fieldsToUpdate);
  };

  const handleAnswersChange = (answers) => {
    updateQuestion({ answers });
  };

  const handleRandomizeAnswerOrderChange = (randomizeOrder) => {
    updateQuestion({ randomize_answer_order: randomizeOrder });
  };

  const handlePinLastToBottomChange = (pinLastToBottom) => {
    updateQuestion({ pin_last_to_bottom: pinLastToBottom });
  };

  const handleLabelsChange = (labels) => {
    updateQuestion({ labels });
  };

  const handleDescriptionChange = (description) => {
    updateQuestion({ description });
  };

  const isTitleAndDescription = isTitleAndDescriptionType(question.type);

  function getNext(option) {
    switch (option.type) {
      case 'by-order':
        return 'byOrder';
      case 'thank-you':
        return 'thankYou';
      case 'survey-question':
        return `question:${option.question.uuid}`;
      case 'based-on-answer':
        return 'byAnswer';
      default:
        return undefined;
    }
  }

  function getNextByAnswer(option) {
    switch (option.type) {
      case 'based-on-answer':
        return option.options.map((q) => getNext(q));
      default:
        return undefined;
    }
  }

  const { hasError, errorMessage } = useMemo(() => {
    const textQuestionAsksForPassword =
      QUESTIONS_WITH_TEXT_INPUT.includes(question.type) && /password/i.test(question.text);
    return {
      hasError: textQuestionAsksForPassword || question.text?.length === 0,
      errorMessage: textQuestionAsksForPassword
        ? t('sensitive-data-error', { sensitiveData: t('passwords') })
        : null,
    };
  }, [question.text, t, question.type]);

  const imageURL = 'image' in question ? question.image : question.image_url;

  return (
    <QuestionContainer index={index} onMouseEnter={reloadPreviewCallback} isDragging={isDragging}>
      <QuestionWrapper>
        <QuestionHeader>
          <QuestionTypeSelector
            isDisabled={!canChangeQuestionType}
            selectedType={question.type}
            onChange={handleQuestionTypeChange}
          />
          <QuestionActions>
            {!isTitleAndDescription && (
              <StyledSwitch
                name="required"
                size="s"
                labelContent={<span>{t('required')}</span>}
                isChecked={question.required}
                onChange={handleRequiredChange}
              />
            )}
            {removable && (
              <IconButton
                icon="trash"
                description={t('delete-question')}
                tooltipText={t('delete-question')}
                size="s"
                onClick={() => onRemove && onRemove(question)}
              />
            )}
          </QuestionActions>
        </QuestionHeader>
        <TextField
          id={`question-text-${index}`}
          name="text"
          rtl={rtl}
          value={question.text}
          onChange={handleQuestionTextChange}
          onFocus={reloadPreviewCallback}
          placeholder={
            isTitleAndDescription ? t('default-statement-title') : t('default-question-title')
          }
          aria-label={
            isTitleAndDescription ? t('default-statement-title') : t('default-question-title')
          }
          hasError={validate && hasError}
          isError={hasError}
          errorMessage={errorMessage}
        />
      </QuestionWrapper>

      {hasImageFeature && (
        <AddImageButton
          imageURL={imageURL}
          onChangeImage={({ imageBase64, imageObjectURL }) =>
            updateQuestion({
              image: imageBase64,
              image_url: imageObjectURL,
              image_thumbnail_url: imageBase64,
            })
          }
        />
      )}

      {isCloseEndedType(question.type) && (
        <Answers
          answers={question.answers}
          onChange={handleAnswersChange}
          onAddAnswer={handleAddAnswer}
          onDeleteAnswer={handleDeleteAnswer}
          onRandomizeOrderChange={handleRandomizeAnswerOrderChange}
          randomizeAnswerOrder={question.randomize_answer_order}
          onPinLastToBottomChange={handlePinLastToBottomChange}
          pinLastToBottom={question.pin_last_to_bottom}
          validate={validate}
          rtl={rtl}
        />
      )}
      {question.type === 'reaction' && (
        <ReactionStyle
          question={question}
          updateQuestion={updateQuestion}
          onPreviewReaction={onPreviewReaction}
        />
      )}
      {isScaleType(question.type) && (
        <ScaleLabels labels={question.labels} onChange={handleLabelsChange} validate={validate} />
      )}
      {isTitleAndDescription && (
        <Description description={question.description} onChange={handleDescriptionChange} />
      )}
      <QuestionFooter>
        <NextQuestionSelector
          question={question}
          questions={questions}
          onChange={(option) => {
            if (option.type === 'based-on-answer') {
              updateQuestion({
                next: 'byAnswer',
                nextByAnswer: getNextByAnswer(option),
              });
              return;
            }

            updateQuestion({
              next: getNext(option),
              nextByAnswer: undefined,
            });
          }}
          onSkippedChange={(option) => {
            updateQuestion({
              nextIfSkipped: getNext(option),
            });
          }}
        />
      </QuestionFooter>
    </QuestionContainer>
  );
};
